import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send, Settings, Bot, User, Workflow, MessageCircle, Zap, DollarSign } from 'lucide-react';
import MainLayout from '../Layout/MainLayout';
import V2ChatMessages from '../V2Chat/V2ChatMessages';
import { useAuth } from '../../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';

const ChatPlayground = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  
  // Get sessionId from URL params if available
  const urlSessionId = searchParams.get('sessionId');
  
  // State management
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [selectedModel, setSelectedModel] = useState('gpt-4o');
  const [sessionId, setSessionId] = useState(urlSessionId);
  const [wsConnection, setWsConnection] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [showSettings, setShowSettings] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [totalTokens, setTotalTokens] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  
  // Prompt selection states
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Provider configurations
  const providers = {
    claude: {
      name: 'Anthropic Claude',
      icon: '🧠',
      models: [
        { id: 'claude-3-opus-20240229', name: 'Claude 3 Opus', cost: { input: 0.015, output: 0.075 } },
        { id: 'claude-3-sonnet-20240229', name: 'Claude 3 Sonnet', cost: { input: 0.003, output: 0.015 } },
        { id: 'claude-3-haiku-20240307', name: 'Claude 3 Haiku', cost: { input: 0.00025, output: 0.00125 } }
      ]
    },
    openai: {
      name: 'OpenAI GPT',
      icon: '🤖',
      models: [
        { id: 'gpt-4', name: 'GPT-4', cost: { input: 0.03, output: 0.06 } },
        { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', cost: { input: 0.01, output: 0.03 } },
        { id: 'gpt-4o', name: 'GPT-4o', cost: { input: 0.005, output: 0.015 } },
        { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', cost: { input: 0.0015, output: 0.002 } }
      ]
    }
  };

  // Available workflows
  const availableWorkflows = [
    {
      id: 'bill_processing_agent',
      name: 'Bill Processing Agent V5',
      description: 'Process bills with expense account tool integration',
      icon: '📄'
    },
    {
      id: 'general_assistant',
      name: 'General Assistant',
      description: 'General purpose AI assistant for various tasks',
      icon: '🤖'
    }
  ];

  // Fetch available prompts
  const fetchPrompts = async () => {
    setIsLoadingPrompts(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/prompts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailablePrompts(data.prompts || []);
        console.log('Loaded prompts:', data.prompts);
      } else {
        console.error('Failed to fetch prompts:', response.status);
      }
    } catch (error) {
      console.error('Error fetching prompts:', error);
    } finally {
      setIsLoadingPrompts(false);
    }
  };

  // Load prompts on component mount
  useEffect(() => {
    if (token) {
      fetchPrompts();
    }
  }, [token]);

  // Initialize session
  useEffect(() => {
    if (urlSessionId) {
      // Continue existing session
      loadExistingSession(urlSessionId);
    } else {
      // Don't create session automatically - wait for first message
      // Just show welcome message
      setMessages([{
        id: 'welcome',
        type: 'system',
        content: '🎯 Welcome to the Prompt Playground! Select a prompt and start your conversation.',
        timestamp: new Date().toISOString()
      }]);
    }
    
    return () => {
      if (wsConnection) {
        wsConnection.close();
      }
    };
  }, [urlSessionId]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadExistingSession = async (sessionIdToLoad) => {
    try {
      console.log('Loading session:', sessionIdToLoad);
      
      // Load session messages
      const response = await fetch(`${API_BASE_URL}/api/chat/sessions/${sessionIdToLoad}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Loaded session messages:', data);
        
        // Transform messages to match our format
        const transformedMessages = (data.messages || []).map(msg => ({
          id: msg.id || msg.message_id,
          type: msg.type || msg.message_type || 'assistant',
          content: msg.content,
          timestamp: msg.timestamp || msg.created_at,
          metadata: msg.metadata
        }));
        
        setMessages(transformedMessages);
        setSessionId(sessionIdToLoad);
        connectWebSocket(sessionIdToLoad);
        
        // Add a system message indicating session continuation
        if (transformedMessages.length > 0) {
          setMessages(prev => [...prev, {
            id: 'session-continued',
            type: 'system',
            content: '📋 Session resumed. Previous conversation loaded.',
            timestamp: new Date().toISOString()
          }]);
        } else {
          // Empty session, add welcome message
          setMessages([{
            id: 'session-empty',
            type: 'system',
            content: '📋 Session loaded. This conversation has no previous messages.',
            timestamp: new Date().toISOString()
          }]);
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to load session:', response.status, errorData);
        // If session doesn't exist, show welcome message - don't create session yet
        setMessages([{
          id: 'session-not-found',
          type: 'system',
          content: '⚠️ Session not found. Select a prompt and start a new conversation.',
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Failed to load existing session:', error);
      // Fallback to showing welcome message - don't create session yet
      setMessages([{
        id: 'welcome-error',
        type: 'system',
        content: '⚠️ Could not load session. Select a prompt and start a new conversation.',
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const createChatSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          agent_id: "1", // API expects string ID
          session_name: `Chat Session ${new Date().toLocaleTimeString()}`
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Session created:', data);
        setSessionId(data.session_id);
        connectWebSocket(data.session_id);
        
        // Remove welcome message when session is created (user is typing)
        // Don't add duplicate welcome message
        
        return data.session_id; // Return session ID for immediate use
      } else {
        const errorData = await response.json();
        console.error('Failed to create session:', response.status, errorData);
        return null;
      }
    } catch (error) {
      console.error('Failed to create chat session:', error);
      return null;
    }
  };

  const connectWebSocket = (sessionIdToConnect) => {
    if (wsConnection) {
      wsConnection.close();
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/chat/ws/${sessionIdToConnect}?token=${token}`;
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      setConnectionStatus('connected');
      setWsConnection(ws);
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWebSocketMessage(data);
    };
    
    ws.onclose = () => {
      setConnectionStatus('disconnected');
      setWsConnection(null);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnectionStatus('error');
    };
  };

  const handleWebSocketMessage = (data) => {
    if (data.type === 'message' || data.type === 'assistant_message') {
      // Only add assistant messages through WebSocket, not user messages (they're added locally)
      if (data.message_type === 'assistant' || data.type === 'assistant_message') {
        const newMessage = {
          id: data.message_id || Date.now(),
          type: 'assistant',
          content: data.content,
          timestamp: data.timestamp || new Date().toISOString(),
          metadata: data.metadata
        };
        
        setMessages(prev => [...prev, newMessage]);
      }
      setIsLoading(false);
      
      // Update token usage
      if (data.metadata?.tokens_used) {
        setTotalTokens(prev => prev + data.metadata.tokens_used);
      }
      if (data.metadata?.cost) {
        setTotalCost(prev => prev + data.metadata.cost);
      }
    } else if (data.type === 'workflow_step') {
      // Handle workflow step updates
      const stepMessage = {
        id: Date.now(),
        type: 'workflow',
        content: data.content,
        timestamp: data.timestamp,
        metadata: data.metadata
      };
      setMessages(prev => [...prev, stepMessage]);
    } else if (data.type === 'error') {
      console.error('Chat error:', data.error);
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Create session if it doesn't exist (first message)
    let currentSessionId = sessionId;
    if (!currentSessionId) {
      currentSessionId = await createChatSession();
      if (!currentSessionId) {
        console.error('Failed to create session');
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: currentSessionId,
          message: messageToSend,
          prompt_id: selectedPromptId || undefined,
          provider: selectedProvider, // Force OpenAI
          model: selectedModel // Override prompt's model with UI selection
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Error: ${error.message}`,
        timestamp: new Date().toISOString()
      }]);
      setIsLoading(false);
    }
  };

  const executeWorkflow = async (workflowId) => {
    if (!sessionId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/workflows/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          workflow_id: workflowId,
          provider: selectedProvider,
          model: selectedModel,
          api_key: apiKey || undefined
        })
      });

      if (!response.ok) {
        throw new Error('Failed to execute workflow');
      }

      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'system',
        content: `🚀 Starting workflow: ${workflowId}`,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Failed to execute workflow:', error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Failed to execute workflow: ${error.message}`,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'user': return <User className="w-4 h-4" />;
      case 'assistant': return <Bot className="w-4 h-4" />;
      case 'workflow': return <Workflow className="w-4 h-4" />;
      case 'system': return <MessageCircle className="w-4 h-4" />;
      case 'error': return <span className="text-red-500">❌</span>;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  return (
    <MainLayout title="Prompt Playground">
      <div className="flex flex-col h-[calc(100vh-8rem)] bg-white dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 relative z-10">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <MessageCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Prompt Playground</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">AI Chat & Workflow Execution</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center">
                <Zap className="w-3 h-3 mr-1" />
                {totalTokens} tokens
              </span>
              <span className="flex items-center">
                <DollarSign className="w-3 h-3 mr-1" />
                ${totalCost.toFixed(4)}
              </span>
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                connectionStatus === 'connected' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  connectionStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'
                }`} />
                <span>{connectionStatus}</span>
              </div>
            </div>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="w-80 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col">
            {/* Prompt Selection */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 relative z-50">
              <h3 className="font-medium text-gray-900 dark:text-white mb-3">Prompt Configuration</h3>
              
              <div className="relative z-50">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select Prompt
                </label>
                <select
                  value={selectedPromptId || ''}
                  onChange={(e) => setSelectedPromptId(e.target.value || null)}
                  disabled={isLoadingPrompts}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 relative z-50"
                >
                  <option value="">
                    {isLoadingPrompts ? 'Loading prompts...' : 'Select a prompt (optional)'}
                  </option>
                  {availablePrompts.map((prompt) => (
                    <option key={prompt.prompt_id} value={prompt.prompt_id}>
                      {prompt.name} (Agent {prompt.agent_id})
                    </option>
                  ))}
                </select>
                {selectedPromptId && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {availablePrompts.find(p => p.prompt_id === selectedPromptId)?.description || 'Custom prompt selected'}
                  </p>
                )}
              </div>
            </div>

            {/* Provider & Model Selection */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <h3 className="font-medium text-gray-900 dark:text-white mb-3">AI Configuration</h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Provider</label>
                  <div className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white">
                    🤖 OpenAI GPT (Fixed)
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Model</label>
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    {providers.openai.models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">API Key (Optional)</label>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Enter your API key..."
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Leave empty to use platform keys</p>
                </div>
              </div>
            </div>

            {/* Available Workflows */}
            <div className="p-4 flex-1">
              <h3 className="font-medium text-gray-900 dark:text-white mb-3">Available Workflows</h3>
              <div className="space-y-2">
                {availableWorkflows.map((workflow) => (
                  <button
                    key={workflow.id}
                    onClick={() => executeWorkflow(workflow.id)}
                    className="w-full p-3 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{workflow.icon}</span>
                      <div>
                        <div className="font-medium text-sm text-gray-900 dark:text-white">{workflow.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{workflow.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="text-white text-2xl w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Ready to Chat!
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {urlSessionId ? 'Session loaded and ready' : 'Start a new conversation or execute a workflow'}
                  </p>
                </div>
              )}
              
              {messages.map((message) => (
                <div key={message.id} className={`flex items-start space-x-3 ${
                  message.type === 'user' ? 'justify-end' : 'justify-start'
                }`}>
                  {message.type !== 'user' && (
                    <div className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                      {getMessageIcon(message.type)}
                    </div>
                  )}
                  <div className={`max-w-3xl ${
                    message.type === 'user' 
                      ? 'bg-blue-600 text-white' 
                      : message.type === 'error'
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800'
                      : message.type === 'system'
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
                      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700'
                  } rounded-lg p-3`}>
                    <div className="whitespace-pre-wrap">{message.content}</div>
                    <div className="flex items-center justify-between mt-2 text-xs opacity-70">
                      <span>{formatTimestamp(message.timestamp)}</span>
                      {message.metadata?.tokens_used && (
                        <span className="flex items-center">
                          <Zap className="w-3 h-3 mr-1" />
                          {message.metadata.tokens_used} tokens
                        </span>
                      )}
                    </div>
                  </div>
                  {message.type === 'user' && (
                    <div className="flex-shrink-0 p-2 bg-blue-100 dark:bg-blue-900 rounded-full">
                      {getMessageIcon(message.type)}
                    </div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                  <span>AI is thinking...</span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <div className="flex space-x-2">
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message to start a conversation..."
                  disabled={isLoading}
                  className="flex-1 p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  rows={3}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              <div className="flex justify-between items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                <span>Press Enter to send, Shift+Enter for new line</span>
                {sessionId && <span>Session: {sessionId.substring(0, 8)}...</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ChatPlayground;